import React, { useState, useEffect, useCallback } from 'react'
import { Spin, Input, Pagination } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { COLORS } from 'theme'
import styled from 'styled-components'
import adminRequest from 'utils/adminRequest'
import AntdAvatar from 'components/Avatar'

import {
  SearchSection,
  ShowingText,
  TableTop,
  CustomStyledTable,
} from '../../Style/style'

const StyledSearchInput = styled(Input)`
  height: 48px;
  border: none;
  box-shadow: none !important;

  &:focus,
  &:hover,
  &.ant-input-affix-wrapper-focused {
    border: none;
    box-shadow: none !important;
  }

  .ant-input {
    height: 100%;
    border: none;
    box-shadow: none !important;

    &:focus {
      box-shadow: none !important;
    }
  }
`

const CompanyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`

const CompanyName = styled.span`
  font-weight: 700;
  color: ${COLORS.PRIMARY || '#1B2B65'};
  font-size: 14px;
`

const CompanyCity = styled.span`
  font-size: 13px;
  color: ${COLORS.TEXT_MEDIUM || '#6b7280'};
`

const ActiveCorporateList = ({ onSelectCorporate }) => {
  const [corporates, setCorporates] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const pageLimit = 10

  const fetchCorporates = useCallback(async () => {
    setLoading(true)
    try {
      const params = { pageLimit, pageNo: page }
      if (search) params.searchBy = search
      const response = await adminRequest.get('/assessment/getSubscribedCorporates', { params })
      const data = response?.data?.data
      setCorporates((data?.data || []).map(c => ({
        id: c.corporate_id,
        name: c.company_name,
        logo: c.company_logo,
        city: c.city,
        state: c.state,
        isActive: c.is_active,
        subscription_type: c.subscription_type,
      })))
      setTotalCount(data?.count || 0)
    } catch (error) {
      console.error('Error fetching corporates:', error)
      setCorporates([])
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => {
    fetchCorporates()
  }, [fetchCorporates])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput)
      setPage(0)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput])

  const columns = [
    {
      title: 'COMPANY',
      key: 'name',
      width: '35%',
      render: (_, record) => (
        <CompanyInfo onClick={() => onSelectCorporate(record)}>
          <AntdAvatar
            src={record?.logo}
            size={37}
            IconName={record?.name?.charAt(0)}
            font={18}
          />
          <div>
            <CompanyName>{record.name || '-'}</CompanyName>
            {record.city && <div><CompanyCity>{record.city}</CompanyCity></div>}
          </div>
        </CompanyInfo>
      ),
    },
    {
      title: 'CITY',
      key: 'city',
      width: 180,
      render: (_, record) => (
        <span style={{ fontWeight: 500, color: '#000' }}>
          {record.city || '-'}
        </span>
      ),
    },
    {
      title: 'STATE',
      key: 'state',
      width: 180,
      render: (_, record) => (
        <span style={{ fontWeight: 500, color: '#000' }}>
          {record.state || '-'}
        </span>
      ),
    },
    {
      title: 'STATUS',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <span style={{
          color: record.isActive ? '#52c41a' : '#f5222d',
          fontWeight: 500,
        }}>
          {record.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <TableTop>
        <SearchSection>
          <StyledSearchInput
            placeholder="Search by Company Name"
            prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </SearchSection>
        <ShowingText>
          Showing {corporates.length} of {totalCount}
        </ShowingText>
      </TableTop>

      <Spin spinning={loading}>
        <CustomStyledTable
          columns={columns}
          dataSource={corporates}
          rowKey="id"
          pagination={false}
          scroll={{ x: '100%' }}
          bordered
          locale={{
            emptyText: <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No corporates found</div>,
          }}
        />
      </Spin>

      {totalCount > pageLimit && (
        <div style={{ margin: '16px', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
          <Pagination
            current={page + 1}
            total={totalCount}
            pageSize={pageLimit}
            onChange={(p) => setPage(p - 1)}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} corporates`}
          />
        </div>
      )}
    </div>
  )
}

export default ActiveCorporateList
